import * as React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import mapbox from 'mapbox-gl'
import inside from '@turf/inside'
import * as turf from '@turf/helpers'
import bboxPolygon from '@turf/bbox-polygon'
import * as MapContext from 'contexts/map'
import * as Mapbox from 'utils/mapbox'
import * as NumberUtils from '@avcan/utils/number'
import * as GeoUtils from '@avcan/utils/geo'
import place from 'components/icons/place.svg'
import Null from 'components/Null'
import styles from './GeoLocation.module.css'

export default Mapbox.supported() ? Map : Null

Map.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.shape({
        longitude: PropTypes.number,
        latitude: PropTypes.number,
    }).isRequired,
}

function Map({ value = {}, onChange }) {
    const { longitude, latitude } = value
    const options = React.useMemo(() => {
        const center = [
            NumberUtils.isValid(longitude) ? longitude : -125.15,
            NumberUtils.isValid(latitude) ? latitude : 54.8,
        ]
        const zoom = NumberUtils.isValid(longitude) ? 8 : 4

        return { center, zoom }
    }, [])
    const changeValue = React.useCallback(
        ({ lng, lat }) => {
            onChange({
                longitude: NumberUtils.round(lng, 5),
                latitude: NumberUtils.round(lat, 5),
            })
        },
        [onChange]
    )
    const lnglat = React.useMemo(() => {
        if (areValidCoordinates(longitude, latitude)) {
            return new mapbox.LngLat(longitude, latitude)
        }

        return null
    }, [longitude, latitude])
    const on = React.useMemo(
        () => ({
            click(event) {
                changeValue(event.lngLat.wrap())
            },
        }),
        [changeValue]
    )

    return (
        <div className={styles.Map}>
            <MapContext.Map options={options} on={on}>
                <MapContext.NavigationControl />
                <MapContext.FullscreenControl />
                <MapContext.GeolocateControl options={GEOLOCATE_CONTROL_OPTIONS} />
                {lnglat && (
                    <MapContext.WithMap>
                        <Marker lnglat={lnglat} onChange={changeValue} />
                    </MapContext.WithMap>
                )}
            </MapContext.Map>
        </div>
    )
}

// Utils & constants
function Marker({ lnglat, onChange }) {
    const intl = useIntl()
    const map = MapContext.useMap()
    const on = React.useMemo(
        () => ({
            dragend(event) {
                onChange(event.target.getLngLat())
            },
        }),
        [onChange]
    )
    const alt = intl.formatMessage({
        defaultMessage: 'Mountain Information Network PIN',
        description: 'Mountain Information Network submit/edit location map',
    })
    const title = React.useMemo(() => GeoUtils.format(...lnglat.toArray(), 5), [lnglat])

    React.useEffect(() => {
        const bounds = map.getBounds()
        const polygon = bboxPolygon(bounds.toArray().flat())
        const point = turf.point(lnglat.toArray())

        if (inside(point, polygon)) {
            return
        }

        // Fly to only if marker is outside map boundaries
        map.flyTo({
            center: lnglat,
            speed: 1,
        })
    }, [map, lnglat])

    return (
        <MapContext.Marker lnglat={lnglat} options={MARKER_OPTIONS} on={on}>
            <img src={place.src} alt={alt} title={title} />
        </MapContext.Marker>
    )
}
function areValidCoordinates(longitude, latitude) {
    return GeoUtils.isValidLongitude(longitude) && GeoUtils.isValidLatitude(latitude)
}
const GEOLOCATE_CONTROL_OPTIONS = {
    fitBoundsOptions: {
        maxZoom: 10,
    },
}
const MARKER_OPTIONS = {
    draggable: true,
}
